import apiClient from './client'
import type {
  UserSettings,
  DeploymentProfile,
  LlmSettingsBundle,
  LlmSettingsUpdate,
} from '@/types'

export async function getSettings(): Promise<UserSettings> {
  const { data } = await apiClient.get('/users/settings')
  return data.settings
}

export async function saveSettings(settings: Partial<UserSettings>) {
  const { data } = await apiClient.post('/users/settings', settings)
  return data
}

// Deployment Profiles

export async function listDeploymentProfiles(): Promise<DeploymentProfile[]> {
  const { data } = await apiClient.get('/users/deployment-profiles')
  return data.profiles
}

export async function getDeploymentProfile(id: number): Promise<DeploymentProfile> {
  const { data } = await apiClient.get(`/users/deployment-profiles/${id}`)
  return data.profile
}

export async function createDeploymentProfile(profile: Partial<DeploymentProfile>): Promise<DeploymentProfile> {
  const { data } = await apiClient.post('/users/deployment-profiles', profile)
  return data.profile
}

export async function updateDeploymentProfile(id: number, profile: Partial<DeploymentProfile>): Promise<DeploymentProfile> {
  const { data } = await apiClient.put(`/users/deployment-profiles/${id}`, profile)
  return data.profile
}

export async function deleteDeploymentProfile(id: number): Promise<void> {
  await apiClient.delete(`/users/deployment-profiles/${id}`)
}

export async function activateDeploymentProfile(id: number): Promise<DeploymentProfile> {
  const { data } = await apiClient.post(`/users/deployment-profiles/${id}/activate`)
  return data.profile
}

// AI usage (Billing → Usage). Reported in CREDITS only — the backend strips
// every dollar figure from these responses; the instance-admin console is the
// one surface that sees provider cost.


export async function getUsageSummary() {
  const { data } = await apiClient.get('/users/usage/summary')
  return data
}

export interface UsageFilters {
  days?: number
  projects?: string[]
  task_types?: string[]
  obj_names?: string[]
  date_from?: string
  date_to?: string
}

export async function getUsageDetails(filters: UsageFilters = {}) {
  const params = new URLSearchParams()
  if (filters.days) params.set('days', String(filters.days))
  if (filters.projects?.length) params.set('projects', filters.projects.join(','))
  if (filters.task_types?.length) params.set('task_types', filters.task_types.join(','))
  if (filters.obj_names?.length) params.set('obj_names', filters.obj_names.join(','))
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  const { data } = await apiClient.get(`/users/usage/details?${params.toString()}`)
  return data
}

export async function getUsageFilterOptions(): Promise<{ projects: string[]; task_types: string[]; obj_names: string[] }> {
  const { data } = await apiClient.get('/users/usage/filter-options')
  return data
}

export async function checkUsageLimit() {
  const { data } = await apiClient.get('/users/usage/check')
  return data
}

// Ask-AI prompt log (recent prompts + up-arrow recall)

export interface PromptLogEntry {
  prompt_id: string
  created_at: string | null
  project: string | null
  backend: string
  // "user" = typed in the dock; "system" = internally generated turn
  source: 'user' | 'system'
  prompt_text: string
  status: string
  model: string | null
  thinking_level: string | null
  input_tokens: number
  output_tokens: number
  /** What this turn consumed, in credits. These endpoints never return
   *  dollars — provider cost is an operator figure, not a user-facing one. */
  credits: number
  duration_ms: number | null
  calls: number
}

export interface PromptLogDetail extends PromptLogEntry {
  session_id: string | null
  response_text: string | null
  call_breakdown: Array<{
    id: number
    created_at: string | null
    provider: string
    model: string
    task_type: string
    obj_name: string | null
    input_tokens: number
    output_tokens: number
    credits: number
    duration_ms: number | null
  }>
}

export async function getRecentPrompts(
  opts: { project?: string; days?: number; limit?: number; userOnly?: boolean } = {},
): Promise<PromptLogEntry[]> {
  const params = new URLSearchParams()
  if (opts.project) params.set('project', opts.project)
  if (opts.days) params.set('days', String(opts.days))
  if (opts.limit) params.set('limit', String(opts.limit))
  // Only prompts the user actually typed — excludes system-generated turns
  // (verify-gate corrective passes, clicked question answers). Used by the
  // dock's up-arrow recall; the Billing usage list shows everything.
  if (opts.userOnly) params.set('user_only', 'true')
  const qs = params.toString()
  const { data } = await apiClient.get(`/users/usage/prompts${qs ? `?${qs}` : ''}`)
  return data.prompts || []
}

export async function getPromptDetail(promptId: string): Promise<PromptLogDetail> {
  const { data } = await apiClient.get(`/users/usage/prompts/${promptId}`)
  return data
}

// ---- New LLM bundle (Settings → LLM tab) -------------------------------

export async function getLlmBundle(): Promise<LlmSettingsBundle> {
  const { data } = await apiClient.get('/users/settings/llm')
  return data.llm
}

export async function updateLlmBundle(payload: LlmSettingsUpdate): Promise<LlmSettingsBundle> {
  const { data } = await apiClient.put('/users/settings/llm', payload)
  return data.llm
}

/** One-step AI setup from the out-of-credits card (see
 *  backend services/llm_quick_setup): the user's own Anthropic/OpenAI key, a
 *  free OpenCode model, or back to Twasta's AI. Writes the PERSONAL bundle. */
export type QuickSetupRequest =
  | { kind: 'own_key'; provider: 'anthropic' | 'openai'; api_key: string }
  | { kind: 'free_model'; model?: string }
  | { kind: 'platform' }

export async function quickSetupLlm(
  req: QuickSetupRequest,
): Promise<{ kind: string; config_name: string; label: string; repinned?: string[] }> {
  const { data } = await apiClient.post('/users/settings/llm/quick-setup', req)
  return data.result
}

/** One recommended {model, thinking, effort, tier} per task type. */
export interface TaskRecommendation {
  model: string
  thinking: 'off' | 'low' | 'medium' | 'high'
  effort: string
  tier: string
}

/**
 * Recommended per-task model + thinking for a provider (api mode) or agent
 * (agent mode) — powers the "Use recommended" button in the LLM config editor
 * and the instance-admin platform table. Suggestion only; applied only if the
 * user/admin saves. Pass exactly one of provider / agent.
 */
export async function getLlmRecommendations(
  target: { provider?: string; agent?: string },
): Promise<Record<string, TaskRecommendation>> {
  const { data } = await apiClient.get('/users/settings/llm/recommendations', {
    params: { provider: target.provider || '', agent: target.agent || '' },
  })
  return data.recommendations || {}
}

/**
 * Verify a provider API key — used by per-provider Test buttons in
 * Settings → LLM. The key is sent in plaintext but not stored; the
 * backend uses it only for the test call.
 */
export async function testProviderKey(
  provider: 'openai' | 'anthropic' | 'google' | 'deepseek',
  apiKey: string,
): Promise<{ ok: boolean; error: string | null }> {
  const { data } = await apiClient.post(
    `/users/settings/llm/test-provider/${provider}`,
    { api_key: apiKey },
  )
  return data
}

// ---- Claude subscription OAuth connect flow -----------------------------
// The backend wraps `claude setup-token` in a server-side pty: /start returns
// the claude.ai authorize URL (opened in a new tab), the user pastes the code
// claude.ai shows back into /complete, and the backend captures + stores the
// long-lived token. See backend claude_oauth_service.py.

/**
 * Connection state for an agent subscription, shared by both connect flows.
 * Which fields are populated depends on what the provider's credential can
 * tell us: Claude's setup-token has an expiry and a token tail, Codex's
 * auth.json carries the signed-in account's email and ChatGPT plan.
 */
export interface AgentConnectStatus {
  connected: boolean
  connected_at?: string | null
  /** Claude: the ~1-year setup-token expiry. */
  expires_at?: string | null
  /** Claude: last 4 characters of the stored token. */
  token_tail?: string | null
  /** Codex: the signed-in ChatGPT account. */
  email?: string | null
  /** Codex: the account's plan (plus, pro, business…). */
  plan?: string | null
  /** Codex: the ChatGPT account id the credential is bound to. */
  account_id?: string | null
}

/** @deprecated Use {@link AgentConnectStatus}; kept so existing imports resolve. */
export type ClaudeOauthStatus = AgentConnectStatus

export async function startClaudeOauth(): Promise<{ flow_id: string; auth_url: string }> {
  const { data } = await apiClient.post('/users/settings/llm/claude-oauth/start')
  return data
}

export async function completeClaudeOauth(
  flowId: string,
  code: string,
  scope: 'tenant' | 'user',
): Promise<{ ok: boolean; validated: boolean; status: ClaudeOauthStatus }> {
  const { data } = await apiClient.post('/users/settings/llm/claude-oauth/complete', {
    flow_id: flowId,
    code,
    scope,
  })
  return data
}

export async function cancelClaudeOauth(flowId: string): Promise<void> {
  await apiClient.post('/users/settings/llm/claude-oauth/cancel', { flow_id: flowId })
}

export async function getClaudeOauthStatus(scope: 'tenant' | 'user'): Promise<ClaudeOauthStatus> {
  const { data } = await apiClient.get(`/users/settings/llm/claude-oauth/status?scope=${scope}`)
  return data
}

export async function disconnectClaudeOauth(scope: 'tenant' | 'user'): Promise<void> {
  await apiClient.delete(`/users/settings/llm/claude-oauth?scope=${scope}`)
}

// ---- Codex subscription device-code connect flow ------------------------
// Codex's default `codex login` is a loopback flow (localhost:1455) a server
// cannot complete, so the backend wraps `codex login --device-auth` instead:
// /start returns OpenAI's device page plus a one-time code, the user enters
// the code THERE (nothing to paste back here), and /poll reports pending until
// the CLI has written the credential. See backend codex_oauth_service.py.

export interface CodexDeviceFlow {
  flow_id: string
  verification_uri: string
  user_code: string
  expires_at: string
}

export async function startCodexOauth(): Promise<CodexDeviceFlow> {
  const { data } = await apiClient.post('/users/settings/llm/codex-oauth/start')
  return data
}

export async function pollCodexOauth(
  flowId: string,
  scope: 'tenant' | 'user',
): Promise<{ state: 'pending' | 'connected'; status?: AgentConnectStatus }> {
  const { data } = await apiClient.post('/users/settings/llm/codex-oauth/poll', {
    flow_id: flowId,
    scope,
  })
  return data
}

export async function cancelCodexOauth(flowId: string): Promise<void> {
  await apiClient.post('/users/settings/llm/codex-oauth/cancel', { flow_id: flowId })
}

export async function getCodexOauthStatus(scope: 'tenant' | 'user'): Promise<AgentConnectStatus> {
  const { data } = await apiClient.get(`/users/settings/llm/codex-oauth/status?scope=${scope}`)
  return data
}

export async function disconnectCodexOauth(scope: 'tenant' | 'user'): Promise<void> {
  await apiClient.delete(`/users/settings/llm/codex-oauth?scope=${scope}`)
}

/**
 * Verify an agent CLI is installed and its credential resolves. Runs
 * ``<agent> --version`` server-side after materializing the credential
 * to the user's per-user dir — no real prompt cost.
 */
export async function testAgent(
  agent: 'claude_code' | 'codex' | 'opencode',
  authMode: 'api_key' | 'subscription' | 'configured',
  credential: string,
  scope?: 'tenant' | 'user',
  model?: string,
): Promise<{ ok: boolean; error: string | null }> {
  // A blank credential in subscription mode tests the STORED one for the
  // scope (stored values round-trip masked, so they can't be resent).
  const { data } = await apiClient.post(`/users/settings/llm/test-agent/${agent}`, {
    auth_mode: authMode,
    credential,
    scope,
    ...(model ? { model } : {}),
  })
  return data
}

export interface OpencodeModelsResponse {
  models: import('@/data/llmCatalog').OpencodeModel[]
  status: { installed: boolean; binary?: string; version?: string; error?: string }
}

/** The models the OpenCode CLI can run on the server — every provider/model
 * the providers configured in OpenCode expose (free OpenCode Zen ones too). */
export async function getOpencodeModels(refresh = false): Promise<OpencodeModelsResponse> {
  const { data } = await apiClient.get('/users/settings/llm/opencode/models', {
    params: refresh ? { refresh: true } : {},
  })
  return data
}

// ---- OpenCode bring-your-own-key ----------------------------------------
// Every provider OpenCode supports (its models.dev catalog, ~230) can run an
// OpenCode configuration on a key the user/organization supplies. Keys are
// write-only: reads only say WHICH providers hold one.

export interface OpencodeProvider {
  id: string
  name: string
  doc: string
  env: string[]
  model_count: number
  /** Runs on one pasted API key (false = needs account id / region / … on the server). */
  key_only: boolean
  /** Already usable in "OpenCode providers" mode on this server. */
  configured_on_server: boolean
}

export interface OpencodeCatalogModel {
  id: string
  provider: string
  name: string
  label: string
  free: boolean
  reasoning: boolean
  context?: number
  cost_in?: number | null
  cost_out?: number | null
  status?: string
}

export async function getOpencodeProviders(
  scope: 'tenant' | 'user',
): Promise<{ providers: OpencodeProvider[]; keys: Record<string, boolean> }> {
  const { data } = await apiClient.get('/users/settings/llm/opencode/providers', { params: { scope } })
  return data
}

export async function getOpencodeProviderModels(provider: string): Promise<OpencodeCatalogModel[]> {
  const { data } = await apiClient.get('/users/settings/llm/opencode/provider-models', { params: { provider } })
  return data.models || []
}

/** Store a key (`key`), or clear it (`key: '-'`). Returns the masked view. */
export async function setOpencodeKey(
  scope: 'tenant' | 'user',
  provider: string,
  key: string,
): Promise<Record<string, boolean>> {
  const { data } = await apiClient.put('/users/settings/llm/opencode/keys', { scope, provider, key })
  return data.keys || {}
}

/** One tiny real prompt on `model` with the given key (blank = the stored one). */
export async function testOpencodeKey(
  scope: 'tenant' | 'user',
  model: string,
  key = '',
): Promise<{ ok: boolean; error: string | null }> {
  const { data } = await apiClient.post('/users/settings/llm/opencode/test-key', { scope, model, key })
  return data
}
